import React from "react";
import { useEffect, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	Paper,
	Radio,
	RadioGroup,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { clientService, IListagemCliente } from "../../../../shared/services/api/client/clientService";
import { ToastContent } from "../../../../shared/components";
import { returnMessageError } from "../../../../shared/utils/returnMessageError";
import { normalizeCreatedFrom, translateCreatedFrom, TCreatedFrom } from "../../utils/translate";

interface TPartnerOption {
	id: string;
	label: string;
	tradeName?: string;
	email?: string;
	typeLabel?: string;
	status?: string;
}

interface PartnersCardProps {
	id: string;
	canActivatePartners?: boolean;
	canChangePartnerOrigin?: boolean;
	createdFrom?: string;
	createdFromPartnerId?: string;
	onCreatedFromUpdated?: (createdFrom: string, partnerId?: string) => void;
}

export const PartnersCard: React.FC<PartnersCardProps> = ({
	id,
	canActivatePartners = false,
	canChangePartnerOrigin = false,
	createdFrom,
	createdFromPartnerId,
	onCreatedFromUpdated,
}) => {
	const [partners, setPartners] = useState<IListagemCliente[]>([]);
	const [Isloading, setIsLoading] = useState<boolean>(true);
	const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
	const [pendingPartner, setPendingPartner] = useState<IListagemCliente | null>(null);
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	const theme = useTheme();

	const [currentCreatedFrom, setCurrentCreatedFrom] = useState<TCreatedFrom>(normalizeCreatedFrom(createdFrom));
	const [currentPartnerId, setCurrentPartnerId] = useState<string | undefined>(createdFromPartnerId);
	const [isOriginDialogOpen, setIsOriginDialogOpen] = useState(false);
	const [originType, setOriginType] = useState<TCreatedFrom>(normalizeCreatedFrom(createdFrom));
	const [originPartner, setOriginPartner] = useState<TPartnerOption | null>(null);
	const [partnerOptions, setPartnerOptions] = useState<TPartnerOption[]>([]);
	const [isLoadingPartnerOptions, setIsLoadingPartnerOptions] = useState(false);
	const [isSavingOrigin, setIsSavingOrigin] = useState(false);

	useEffect(() => {
		setCurrentCreatedFrom(normalizeCreatedFrom(createdFrom));
		setCurrentPartnerId(createdFromPartnerId);
	}, [createdFrom, createdFromPartnerId]);

	type TStatus = "active" | "inactive";
	const normalizeStatus = (raw: unknown): TStatus => {
		const value = String(raw ?? "").toLowerCase();
		if (value === "inactive" || value === "0" || value.includes("inativ")) return "inactive";
		return "active";
	};

	const statusChipSx = (status: TStatus) => {
		if (status === "inactive") {
			return {
				color: "#e40909",
				fontWeight: 500,
				background: "#fce4e4",
				border: "0.5px solid #f1f1f1",
			} as const;
		}

		return {
			color: "#0f8a0d",
			fontWeight: 500,
			background: "#d9f8e5",
			border: "0.5px solid #f1f1f1",
		} as const;
	};

	const getRelationIdFromPartner = (partner: IListagemCliente): string | undefined => {
		const anyPartner = partner as any;
		return (
			partner?.id ||
			anyPartner?.id ||
			anyPartner?.relationId ||
			anyPartner?.clientRelationId ||
			anyPartner?.client_relation_id ||
			anyPartner?.clientRelation?.id ||
			anyPartner?.relation?.id ||
			anyPartner?.partnerId
		);
	};

	const showToastError = (titleMessage: string, contentMessage: string) => {
		toastHandler.present({
			type: "error",
			position: "top-right",
			messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
		});
	};

	const showToastSuccess = (titleMessage: string, contentMessage: string) => {
		toastHandler.present({
			type: "success",
			position: "top-right",
			messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
		});
	};

	const fetchPartnersByClient = async () => {
		try {
			const result = await clientService.getPartners(id);
			if (result instanceof Error) {
				showToastError("Alerta!!", "Erro: " + result?.message);
				return;
			}
			setPartners(result.data);
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			showToastError(titleMessage, contentMessage);
		}
	};

	const getPartnersByClient = (useDebounceCall: boolean = true) => {
		if (id !== "novo") {
			setIsLoading(true);
			const run = async () => {
				await fetchPartnersByClient();
				setIsLoading(false);
			};

			if (useDebounceCall) {
				debounce(run);
			} else {
				run();
			}
		}
	};

	const setPartnerStatus = async (partner: IListagemCliente, status: TStatus) => {
		const relationId = getRelationIdFromPartner(partner);
		if (!relationId) {
			showToastError("Alerta!!", "Não foi possível identificar o ID da relação para atualizar o status.");
			return;
		}

		setIsUpdatingStatus(true);
		try {
			const result = await clientService.updateClientRelationStatus(relationId, status);
			if (result instanceof Error) {
				showToastError("Alerta!!", "Erro: " + result.message);
				return;
			}

			// Mantém o "Tipo de Cadastro" do cliente sincronizado com o parceiro Ativo da tabela,
			// já que só pode existir um parceiro ativo por vez.
			const partnerClientId = (partner as any).partnerId ?? partner.id;
			const newCreatedFrom: TCreatedFrom = status === "active" ? "web_service" : "web_app";
			const newPartnerId = status === "active" ? partnerClientId : "";

			const syncResult = await clientService.updateCreatedFrom(id, {
				createdFrom: newCreatedFrom,
				partnerId: newPartnerId,
			});

			if (!(syncResult instanceof Error)) {
				setCurrentCreatedFrom(newCreatedFrom);
				setCurrentPartnerId(status === "active" ? partnerClientId : undefined);
				onCreatedFromUpdated?.(newCreatedFrom, status === "active" ? partnerClientId : undefined);
			}

			getPartnersByClient(false);
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			showToastError(titleMessage, contentMessage);
		} finally {
			setIsUpdatingStatus(false);
		}
	};

	const deactivateOtherPartners = async (partnerToKeepActive: IListagemCliente) => {
		const otherActives = partners.filter(
			(p) => normalizeStatus((p as any).status) === "active" && p.id !== partnerToKeepActive.id,
		);

		for (const otherPartner of otherActives) {
			const relationId = getRelationIdFromPartner(otherPartner);
			if (!relationId) {
				showToastError("Alerta!!", "Não foi possível identificar o ID de uma das relações para desativar os demais parceiros.");
				throw new Error("Missing relationId for partner");
			}

			const result = await clientService.updateClientRelationStatus(relationId, "inactive");
			if (result instanceof Error) {
				showToastError("Alerta!!", "Erro: " + result.message);
				throw result;
			}
		}
	};

	const findRelationForPartnerClient = (partnerClientId: string): IListagemCliente | undefined => {
		return partners.find((p) => {
			const anyPartner = p as any;
			return p.id === partnerClientId || anyPartner?.partnerId === partnerClientId || anyPartner?.clientId === partnerClientId;
		});
	};

	// Garante que só exista um parceiro Ativo por vez: ao vincular um parceiro via
	// "Alterar Cadastro", desativa qualquer outro que já estivesse ativo na tabela abaixo.
	const syncActivePartnerRelation = async (partnerClientId: string) => {
		const matchingRelation = findRelationForPartnerClient(partnerClientId);

		const otherActives = partners.filter(
			(p) => normalizeStatus((p as any).status) === "active" && p.id !== matchingRelation?.id,
		);

		for (const otherPartner of otherActives) {
			const relationId = getRelationIdFromPartner(otherPartner);
			if (relationId) {
				await clientService.updateClientRelationStatus(relationId, "inactive");
			}
		}

		if (matchingRelation && normalizeStatus((matchingRelation as any).status) !== "active") {
			const relationId = getRelationIdFromPartner(matchingRelation);
			if (relationId) {
				await clientService.updateClientRelationStatus(relationId, "active");
			}
		}

		getPartnersByClient(false);
	};

	const handleChangeActive = (partner: IListagemCliente) => {
		if (!canActivatePartners) return;
		const isActive = normalizeStatus((partner as any).status) === "active";
		if (isActive) {
			setPartnerStatus(partner, "inactive");
			return;
		}

		const hasAnotherActive = partners.some((p) => normalizeStatus((p as any).status) === "active" && p.id !== partner.id);
		if (hasAnotherActive) {
			setPendingPartner(partner);
			setIsConfirmDialogOpen(true);
			return;
		}

		setPartnerStatus(partner, "active");
	};

	useEffect(() => {
		getPartnersByClient();
	}, [id]);

	useEffect(() => {
		fetchPartnerOptions();
	}, []);

	const fetchPartnerOptions = async () => {
		setIsLoadingPartnerOptions(true);
		try {
			const result = await clientService.getTypePartners();
			if (result instanceof Error) {
				setPartnerOptions([]);
				return;
			}
			setPartnerOptions(
				result.data.map((partner) => ({
					id: partner.id,
					label: partner.name,
					tradeName: partner.trade_name,
					email: partner.email,
					typeLabel: (partner as any).clientType?.description,
					status: (partner as any).status,
				})),
			);
		} catch {
			setPartnerOptions([]);
		} finally {
			setIsLoadingPartnerOptions(false);
		}
	};

	const handleOpenOriginDialog = () => {
		if (!canChangePartnerOrigin) return;
		setOriginType(currentCreatedFrom);
		setOriginPartner(partnerOptions.find((partner) => partner.id === currentPartnerId) ?? null);
		setIsOriginDialogOpen(true);
		if (!partnerOptions.length) fetchPartnerOptions();
	};

	const handleCloseOriginDialog = () => {
		if (isSavingOrigin) return;
		setIsOriginDialogOpen(false);
	};

	const handleSaveOrigin = async () => {
		if (!canChangePartnerOrigin) return;
		if (originType === "web_service" && !originPartner) {
			showToastError("Alerta!!", "Selecione um parceiro para vincular a integração.");
			return;
		}

		setIsSavingOrigin(true);
		try {
			const result = await clientService.updateCreatedFrom(id, {
				createdFrom: originType,
				partnerId: originType === "web_service" ? originPartner?.id ?? "" : "",
			});

			if (result instanceof Error) {
				showToastError("Alerta!!", "Erro: " + result.message);
				return;
			}

			const savedPartner = originType === "web_service" ? originPartner : null;
			setCurrentCreatedFrom(originType);
			setCurrentPartnerId(savedPartner?.id);
			onCreatedFromUpdated?.(originType, savedPartner?.id);

			if (savedPartner) {
				try {
					await syncActivePartnerRelation(savedPartner.id);
				} catch {
					showToastError("Alerta!!", "Cadastro atualizado, mas não foi possível sincronizar o status Ativo do parceiro na tabela abaixo.");
				}
			} else {
				getPartnersByClient(false);
			}

			showToastSuccess("Sucesso!", "Cadastro atualizado com sucesso.");
			setIsOriginDialogOpen(false);
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			showToastError(titleMessage, contentMessage);
		} finally {
			setIsSavingOrigin(false);
		}
	};

	// Não faz sentido oferecer, para vincular via "Alterar Cadastro", um parceiro que já
	// possui uma relação com esse cliente (ainda que desativada) — ele já é gerenciado pela tabela abaixo.
	const availablePartnerOptions = partnerOptions.filter(
		(option) => option.id === originPartner?.id || !findRelationForPartnerClient(option.id),
	);

	return (
		<Box sx={{ px: 2, boxSizing: "border-box", width: "100%" }}>
			<Box
				sx={{
					mt: 1,
					width: "100%",
					boxSizing: "border-box",
					padding: 2.5,
					borderRadius: 2,
					backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[50],
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					flexWrap: "wrap",
					gap: 2,
				}}
			>
				<Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
					<Typography variant="subtitle1" color="primary" fontWeight={600}>
						Tipo de Cadastro
					</Typography>
					<Chip
						size="medium"
						label={translateCreatedFrom(currentCreatedFrom)}
						color={currentCreatedFrom === "web_service" ? "info" : "default"}
						variant="outlined"
					/>
				</Stack>

				{canChangePartnerOrigin && (
					<Button variant="outlined" size="small" startIcon={<EditOutlinedIcon />} onClick={handleOpenOriginDialog}>
						Alterar Cadastro
					</Button>
				)}
			</Box>

			<TableContainer
				component={Paper}
				variant="elevation"
				sx={{
					mt: 1,
					width: "100%",
					boxSizing: "border-box",
					px: { xs: 1, sm: 2 },
					py: { xs: 1.5, sm: 2 },
					borderRadius: 2,
					overflow: "auto",
				}}
				aria-label="tabela de parceiros"
			>
				<Table size="small">
					<TableHead>
						<TableRow sx={{ backgroundColor: "grey.100" }}>
							<TableCell>
								<Typography variant="button">Nome</Typography>
							</TableCell>
							<TableCell>
								<Typography variant="button">E-mail</Typography>
							</TableCell>
							<TableCell align="center">
								<Typography variant="button">Status</Typography>
							</TableCell>
							<TableCell align="center">
								<Typography variant="button">Ativo</Typography>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{Isloading ? (
							<TableRow>
								<TableCell sx={{ textAlign: "center", py: 4 }} colSpan={4}>
									<CircularProgress size={28} />
								</TableCell>
							</TableRow>
						) : !partners?.length ? (
							<TableRow>
								<TableCell sx={{ textAlign: "center", py: 4 }} colSpan={4}>
									<Typography variant="body2" color="text.secondary">
										Nenhum parceiro encontrado!
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							partners?.map((partner, index) => (
								<TableRow key={index} hover>
									<TableCell>
										<Typography variant="body2">{partner.name}</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="body2" color="text.secondary">
											{partner.email}
										</Typography>
									</TableCell>
									<TableCell align="center">
										{(() => {
											const derivedStatus = normalizeStatus((partner as any).status);
											return <Chip size="small" label={derivedStatus === "inactive" ? "Inativo" : "Ativo"} sx={statusChipSx(derivedStatus)} />;
										})()}
									</TableCell>
									<TableCell align="center">
										<Switch
											size="small"
											checked={normalizeStatus((partner as any).status) === "active"}
											disabled={Isloading || isUpdatingStatus || !canActivatePartners}
											onChange={() => handleChangeActive(partner)}
											inputProps={{ "aria-label": "Ativar/Desativar parceiro" }}
										/>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>

			<Dialog
				open={isConfirmDialogOpen}
				onClose={() => {
					setIsConfirmDialogOpen(false);
					setPendingPartner(null);
				}}
				disableEscapeKeyDown={isUpdatingStatus}
			>
				<DialogTitle>Atenção</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Ao ativar este parceiro, os demais parceiros serão desativados automaticamente. Deseja continuar?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						disabled={isUpdatingStatus}
						onClick={() => {
							setIsConfirmDialogOpen(false);
							setPendingPartner(null);
						}}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						disabled={isUpdatingStatus || !pendingPartner || !canActivatePartners}
						onClick={async () => {
							if (!pendingPartner) return;
							setIsUpdatingStatus(true);
							try {
								await deactivateOtherPartners(pendingPartner);
								await setPartnerStatus(pendingPartner, "active");
								setIsConfirmDialogOpen(false);
								setPendingPartner(null);
							} catch {
								// Erro já tratado com toast
							} finally {
								setIsUpdatingStatus(false);
							}
						}}
					>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>
			</TableContainer>

			<Dialog open={isOriginDialogOpen} onClose={handleCloseOriginDialog} disableEscapeKeyDown={isSavingOrigin} fullWidth maxWidth="sm">
				<DialogTitle>Alterar Tipo de Cadastro</DialogTitle>
				<DialogContent>
					<Box mt={1}>
						<RadioGroup
							value={originType}
							onChange={(e) => {
								const value = e.target.value as TCreatedFrom;
								setOriginType(value);
								if (value === "web_app") setOriginPartner(null);
							}}
						>
							<FormControlLabel value="web_app" control={<Radio />} label="Aplicação" disabled={isSavingOrigin} />
							<FormControlLabel value="web_service" control={<Radio />} label="Integração" disabled={isSavingOrigin} />
						</RadioGroup>

						{originType === "web_service" && (
							<Box mt={2}>
								<Autocomplete
									options={availablePartnerOptions}
									loading={isLoadingPartnerOptions}
									disabled={isSavingOrigin}
									value={originPartner}
									onChange={(_, value) => setOriginPartner(value)}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									getOptionLabel={(option) => option.label}
									renderOption={(props, option) => {
										const status = normalizeStatus(option.status);
										return (
											<Box
												component="li"
												{...props}
												key={option.id}
												sx={{ display: "flex", flexDirection: "column", alignItems: "stretch !important", gap: 0.4, py: "8px !important" }}
											>
												<Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" gap={1.5}>
													<Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
														{option.label}
													</Typography>
													<Stack direction="row" spacing={1} flexShrink={0}>
														{option.typeLabel && (
															<Chip size="small" variant="outlined" label={option.typeLabel} sx={{ fontWeight: 500 }} />
														)}
														<Chip size="small" label={status === "inactive" ? "Inativo" : "Ativo"} sx={statusChipSx(status)} />
													</Stack>
												</Stack>
												{option.tradeName && (
													<Typography variant="caption" color="text.secondary" noWrap>
														{option.tradeName}
													</Typography>
												)}
												{option.email && (
													<Typography variant="caption" color="text.secondary" noWrap>
														{option.email}
													</Typography>
												)}
											</Box>
										);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Parceiro"
											placeholder="Selecione o parceiro"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isLoadingPartnerOptions ? <CircularProgress color="inherit" size={18} /> : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</Box>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button disabled={isSavingOrigin} onClick={handleCloseOriginDialog}>
						Cancelar
					</Button>
					<Button
						variant="contained"
						disabled={isSavingOrigin || (originType === "web_service" && !originPartner)}
						onClick={handleSaveOrigin}
					>
						{isSavingOrigin ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
